import React, {useState} from 'react';
import {ContactFormConfig} from '../../../../types/ContactForm';
import Input from '../../../../components/Input';
import Select from '../../../../components/Select';
import ColorPicker from '../../../../components/ColorPicker';
import cx from 'classnames';
import styles from './SettingTab.module.scss';
import ProgressBarStyle from "./ProgressBarStyle";
import { GlobalSettings } from './GlobalSettings';

interface SettingTabProps {
    config: ContactFormConfig;
    updateConfig: (property: string, value: any) => void;
}

const SettingTab: React.FC<SettingTabProps> = ({config, updateConfig}) => {
    const [showProgressColorPicker, setShowProgressColorPicker] = useState(false);
    const [showProgressBgColorPicker, setShowProgressBgColorPicker] = useState(false);


    const buttonStyleOptions = [
        {value: 'primary', label: 'Primary'},
        {value: 'secondary', label: 'Secondary'},
        {value: 'outline', label: 'Outline Primary'}
    ];

    const buttonAlignOptions = [
        {value: 'left', label: 'Links'},
        {value: 'center', label: 'Zentriert'},
        {value: 'right', label: 'Rechts'},
        {value: 'full', label: 'Volle Breite'}
    ];


    return (
        <div className={styles.container}>
            {/* Submit Button Section */}
            <div className={cx(styles['info-box'])}>
                <h4>Submit Button</h4>
                <p>
                    Konfigurieren Sie das Aussehen und die Ausrichtung des Absende-Buttons.
                </p>
                <div className={cx(styles['button-preview'], 'zr-mt-4')}>
                    <Input
                        type="text"
                        property="buttonText"
                        value={config.buttonText || 'Senden'}
                        onChange={updateConfig}
                        placeholder="Button Text"
                    />
                    <Select
                        property="buttonStyle"
                        value={config.buttonStyle || 'primary'}
                        onSelect={updateConfig}
                        placeholder="Button Style"
                        options={buttonStyleOptions}
                        searchable={false}
                    />
                    <Select
                        property="buttonAlign"
                        value={config.buttonAlign || 'left'}
                        onSelect={updateConfig}
                        placeholder="Button Ausrichtung"
                        options={buttonAlignOptions}
                        searchable={false}
                    />
                    <Input
                        type="text"
                        property="buttonCssClass"
                        value={config.buttonCssClass || ''}
                        onChange={updateConfig}
                        placeholder="CSS-Klasse für Submit-Button"
                    />
                </div>
            </div>
            {/* Custom CSS Classes Section */}
            <div className={cx(styles['info-box'], 'zr-mt-5')}>
                <h4>Custom CSS Classes</h4>
                <p>
                    Fügen Sie eigene CSS-Klassen für verschiedene Elemente hinzu.
                    Diese können dann in Ihren CSS-Dateien gestylt werden.
                </p>
                <div className={'zr-mb-3 zr-d-flex zr-flex-column zr-gap-4 zr-mt-4'}>
                    <Input
                        type="text"
                        property="formCssClass"
                        value={config.formCssClass || ''}
                        onChange={updateConfig}
                        placeholder="CSS-Klasse für das gesamte Formular"
                    />

                    <Input
                        type="text"
                        property="inputCssClass"
                        value={config.inputCssClass || ''}
                        onChange={updateConfig}
                        placeholder="CSS-Klasse für Input-Felder"
                    />
                </div>
            </div>
            {config.isMultiStep && (
                <>
                    <div className={cx(styles['info-box'], 'zr-mb-4', 'zr-mt-5')}>
                        <h4>Progress Bar Styling</h4>
                        <p>
                            Konfigurieren Sie das Aussehen des Fortschrittsbalken für mehrstufige Formulare.
                        </p>
                        <div className="zr-mb-3 zr-mt-4">
                            <Input
                                type="number"
                                property="progressBarHeight"
                                value={config.progressBarHeight || 12}
                                onChange={updateConfig}
                                placeholder="Höhe in Pixel (für Bars und Punkte)"
                                withCount={true}
                            />
                        </div>
                        <div className="zr-d-flex zr-items-start zr-gap-3">
                            <div className="zr-mb-3">
                                <label className="zr-d-block zr-mb-2">Fortschrittsbalken Farbe</label>
                                <div className="zr-d-flex zr-items-center zr-gap-3">
                                    <div
                                        style={{
                                            width: '40px',
                                            height: '40px',
                                            backgroundColor: config.progressBarColor || '#007cba',
                                            border: '1px solid #ddd',
                                            borderRadius: '4px',
                                            cursor: 'pointer'
                                        }}
                                        onClick={() => setShowProgressColorPicker(!showProgressColorPicker)}
                                    />
                                    <span>{config.progressBarColor || '#007cba'}</span>
                                </div>
                                {showProgressColorPicker && (
                                    <div className="zr-mt-2">
                                        <ColorPicker
                                            property="progressBarColor"
                                            value={config.progressBarColor || '#007cba'}
                                            onChange={updateConfig}
                                        />
                                    </div>
                                )}
                            </div>

                            <div className="zr-mb-3">
                                <label className="zr-d-block zr-mb-2">Hintergrund Farbe</label>
                                <div className="zr-d-flex zr-items-center zr-gap-3">
                                    <div
                                        style={{
                                            width: '40px',
                                            height: '40px',
                                            backgroundColor: config.progressBarBackgroundColor || '#e9ecef',
                                            border: '1px solid #ddd',
                                            borderRadius: '4px',
                                            cursor: 'pointer'
                                        }}
                                        onClick={() => setShowProgressBgColorPicker(!showProgressBgColorPicker)}
                                    />
                                    <span>{config.progressBarBackgroundColor || '#e9ecef'}</span>
                                </div>
                                {showProgressBgColorPicker && (
                                    <div className="zr-mt-2">
                                        <ColorPicker
                                            property="progressBarBackgroundColor"
                                            value={config.progressBarBackgroundColor || '#e9ecef'}
                                            onChange={updateConfig}
                                        />
                                    </div>
                                )}
                            </div>
                        </div>
                        <div className="zr-mt-3">
                            <ProgressBarStyle
                                property="progressBarStyle"
                                value={config.progressBarStyle || 'bar'}
                                onChange={updateConfig}
                                progressBarColor={config.progressBarColor || '#007cba'}
                                progressBarBackgroundColor={config.progressBarBackgroundColor || '#e9ecef'}
                                progressBarHeight={config.progressBarHeight || 4}
                            />
                        </div>
                    </div>
                </>
            )}
            {config.isMultiStep && (
                <>
                    <div className={cx(styles['info-box'], 'zr-mb-4', 'zr-mt-5')}>
                        <h4>Navigation Buttons (Multi-Step)</h4>
                        <p>
                            Konfigurieren Sie die Texte der Weiter/Zurück-Buttons für mehrstufige Formulare.
                        </p>
                        <div className={cx(styles['button-preview'], 'zr-mt-4')}>
                            <Input
                                type="text"
                                property="nextButtonText"
                                value={config.nextButtonText || 'Weiter'}
                                onChange={updateConfig}
                                placeholder="Weiter-Button Text"
                            />
                            <Input
                                type="text"
                                property="prevButtonText"
                                value={config.prevButtonText || 'Zurück'}
                                onChange={updateConfig}
                                placeholder="Zurück-Button Text"
                            />
                            <Input
                                type="text"
                                property="finalButtonText"
                                value={config.finalButtonText || 'Absenden'}
                                onChange={updateConfig}
                                placeholder="Finaler Button Text"
                            />
                            {config.isMultiStep && (
                                <Input
                                    type="text"
                                    property="navButtonCssClass"
                                    value={config.navButtonCssClass || ''}
                                    onChange={updateConfig}
                                    placeholder="CSS-Klasse für Navigation-Buttons"
                                />
                            )}
                        </div>
                    </div>
                </>
            )}
            
            <GlobalSettings config={config} updateConfig={updateConfig} />
        </div>
    );
}

export default SettingTab;